import { useState, useCallback } from 'react';
import { ISMEDesignguidelines } from '../../../Models/ISMEDesignGuidelines';
import { ISMEDesignGuidelineFilter, newSMEDesignGuidelineFilter } from './SMEDesignGuidelineFilter';

// Extended interface to include properties from the API response that aren't in the base interface
export interface ISMEDesignGuidelinesExtended extends ISMEDesignguidelines {
  created?: Date;
  version?: string | number;
}

// Define column interface for Material UI table
export interface Column {
  id: keyof ISMEDesignGuidelinesExtended | 'actions';
  label: string;
  minWidth: number;
  maxWidth: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, item?: ISMEDesignGuidelinesExtended) => React.ReactNode;
  sortable?: boolean;
}

// Dropdown option interface
export interface DropdownOption {
  key: string;
  text: string;
}

// Props interface for the main component
export interface SMEDesignGuidelineProps {
  // Add any props if needed in the future
}

// State interface for the component
export interface SMEDesignGuidelineState {
  // Modal state
  isModalOpen: boolean;
  modalTitle: string;
  isDisabled: boolean;
  
  // Data state
  currentData: ISMEDesignGuidelinesExtended[];
  filteredData: ISMEDesignGuidelinesExtended[];
  dataColumns: Column[];
  isLoading: boolean;
  
  // Sorting state
  order: 'asc' | 'desc';
  orderBy: keyof ISMEDesignGuidelinesExtended | '';
  
  // Filtering state
  showFilters: boolean;
  isFiltering: boolean;
  designGuidelineFilter: ISMEDesignGuidelineFilter;
  
  // Performance optimization
  isInitialApiCallComplete: boolean;
  performanceMetrics: Record<string, number>;
  
  // Dropdown options
  industries: DropdownOption[];
  subIndustries: DropdownOption[];
  applications: DropdownOption[];
  subApplications: DropdownOption[];
  productLines: DropdownOption[];
  processStages: DropdownOption[];
  alfaLavalPositions: DropdownOption[];
  
  // Form state
  designSuggestion: string;
  selectedPosition: string;
  selectedIndustry: string;
  selectedSubIndustry: string;
  selectedApplication: string;
  selectedSubApplication: string;
  selectedProcessStage: string;
  selectedProductLine: string;
}

/**
 * Custom hook for managing SMEDesignGuideline state
 */
export const useSMEDesignGuidelineState = () => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState('');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  
  // Data state
  const [currentData, setCurrentData] = useState<ISMEDesignGuidelinesExtended[]>([]);
  const [filteredData, setFilteredData] = useState<ISMEDesignGuidelinesExtended[]>([]);
  const [dataColumns, setDataColumns] = useState<Column[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Sorting state
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof ISMEDesignGuidelinesExtended | ''>('');
  
  // Filtering state
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [designGuidelineFilter, setDesignGuidelineFilter] = useState<ISMEDesignGuidelineFilter>(newSMEDesignGuidelineFilter());
  
  // Performance optimization
  const [isInitialApiCallComplete, setIsInitialApiCallComplete] = useState<boolean>(false);
  const [performanceMetrics, setPerformanceMetrics] = useState<Record<string, number>>({});
  
  // Dropdown options
  const [industries, setIndustries] = useState<DropdownOption[]>([]);
  const [subIndustries, setSubIndustries] = useState<DropdownOption[]>([]);
  const [applications, setApplications] = useState<DropdownOption[]>([]);
  const [subApplications, setSubApplications] = useState<DropdownOption[]>([]);
  const [productLines, setProductLines] = useState<DropdownOption[]>([]);
  const [processStages, setProcessStages] = useState<DropdownOption[]>([]);
  const [alfaLavalPositions, setAlfaLavalPositions] = useState<DropdownOption[]>([]);
  
  // Form state
  const [designSuggestion, setDesignSuggestion] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [selectedSubIndustry, setSelectedSubIndustry] = useState<string>('');
  const [selectedApplication, setSelectedApplication] = useState<string>('');
  const [selectedSubApplication, setSelectedSubApplication] = useState<string>('');
  const [selectedProcessStage, setSelectedProcessStage] = useState<string>('');
  const [selectedProductLine, setSelectedProductLine] = useState<string>('');

  // Performance logging helper
  const logPerformance = useCallback((label: string, startTime: number) => {
    const duration = performance.now() - startTime;
    setPerformanceMetrics(prev => ({...prev, [label]: duration}));
  }, []);

  // Reset form state
  const resetFormState = useCallback(() => {
    setDesignSuggestion('');
    setSelectedPosition('');
    setSelectedIndustry('');
    setSelectedSubIndustry('');
    setSelectedApplication('');
    setSelectedSubApplication('');
    setSelectedProcessStage('');
    setSelectedProductLine('');
  }, []);

  // Set form state from item
  const setFormStateFromItem = useCallback((item?: ISMEDesignGuidelinesExtended) => {
    setSelectedPosition(item?.alfaLavalPosition || '');
    setSelectedIndustry(item?.industry || '');
    setSelectedApplication(item?.application || '');
    setSelectedSubApplication(item?.subApplication || '');
    setDesignSuggestion(item?.designSuggestion || '');
    setSelectedProcessStage(item?.processStage || '');
    setSelectedProductLine(item?.productLine || '');
    setSelectedSubIndustry(item?.subIndustry || '');
  }, []);

  return {
    // Modal state
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    isDisabled,
    setIsDisabled,
    
    // Data state
    currentData,
    setCurrentData,
    filteredData,
    setFilteredData,
    dataColumns,
    setDataColumns,
    isLoading,
    setIsLoading,
    
    // Sorting state
    order,
    setOrder,
    orderBy,
    setOrderBy,
    
    // Filtering state
    showFilters,
    setShowFilters,
    isFiltering,
    setIsFiltering,
    designGuidelineFilter,
    setDesignGuidelineFilter,
    
    // Performance optimization
    isInitialApiCallComplete,
    setIsInitialApiCallComplete,
    performanceMetrics,
    setPerformanceMetrics,
    logPerformance,
    
    // Dropdown options
    industries,
    setIndustries,
    subIndustries,
    setSubIndustries,
    applications,
    setApplications,
    subApplications,
    setSubApplications,
    productLines,
    setProductLines,
    processStages,
    setProcessStages,
    alfaLavalPositions,
    setAlfaLavalPositions,
    
    // Form state
    designSuggestion,
    setDesignSuggestion,
    selectedPosition,
    setSelectedPosition,
    selectedIndustry,
    setSelectedIndustry,
    selectedSubIndustry,
    setSelectedSubIndustry,
    selectedApplication,
    setSelectedApplication,
    selectedSubApplication,
    setSelectedSubApplication,
    selectedProcessStage,
    setSelectedProcessStage,
    selectedProductLine,
    setSelectedProductLine,
    
    // Form helpers
    resetFormState,
    setFormStateFromItem
  };
}; 